<template>
  <div class="page-content">
    <!-- Loading State -->
    <v-row v-if="loading" style="margin-top: 16px;">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p>Loading payment data...</p>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <template v-else>
      <!-- Tabs -->
      <v-card class="mx-auto elevation-2 tabs-card" style="margin-top: 16px;">
        <v-container fluid class="pa-2">
          <v-row align="center" justify="space-between" no-gutters>
            <v-col cols="auto">
              <v-tabs 
                v-model="activeTab" 
                class="dashboard-tab" 
                show-arrows 
                next-icon="mdi-arrow-right-bold-box-outline" 
                prev-icon="mdi-arrow-left-bold-box-outline"
                style="background-color: white; color: #2C74B3;"
              >
                <v-tab value="studio">Studio</v-tab>
                <v-tab value="morning">Morning</v-tab>
                <v-tab value="evening">Evening</v-tab>
                <v-tab value="night">Night</v-tab>
                <v-tab value="satellite">Satellite</v-tab>
                <v-tab value="lata">Lata</v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <!-- Sexy Summary Card -->
      <v-row style="margin-top: 16px;">
        <v-col cols="12">
          <v-card 
            class="elevation-4 sexy-summary-card" 
            :style="{ height: isCollapsed ? '48px' : '200px', transition: 'height 0.3s ease' }"
            style="border-radius: 12px; overflow: hidden;"
          >
            <div 
              style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; height: 48px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white;"
            >
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="font-size: 18px; font-weight: 700; letter-spacing: 1px;">Payments Dashboard</span>
                <v-icon 
                  @click="toggleCollapse" 
                  style="cursor: pointer; color: white;" 
                  size="24"
                >
                  {{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                </v-icon>
              </div>
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="font-size: 14px; font-style: italic; opacity: 0.8;">Edit Commission</span>
                <v-btn 
                  :class="['btn-secondary']" 
                  style="padding: 0 16px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white; margin-right: 16px" 
                  @click="toggleEditCommissions"
                  :disabled="isPeriodLocked"
                >
                  <v-icon size="20" color="white">mdi-percent</v-icon>
                </v-btn>
                <span style="font-size: 14px; font-style: italic; opacity: 0.8;">Download</span>
                <span 
                  style="cursor: pointer; font-size: 14px; font-weight: 600; color: white; opacity: 0.9;" 
                  @click="toggleDownloadFormat"
                >
                  {{ downloadFormat.toUpperCase() }}
                </span>
                <v-btn 
                  :class="['btn-secondary', downloadFormat === 'pdf' ? 'active-button' : '']" 
                  style="padding: 0 16px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white;" 
                  @click="downloadData"
                >
                  <v-icon size="20">mdi-download</v-icon>
                </v-btn>
              </div>
            </div>

            <v-expand-transition>
              <div 
                v-show="!isCollapsed" 
                style="padding: 12px 16px 16px 16px; display: flex; justify-content: space-between; background-color: white; color: black;"
              >
                <div style="flex: 1; display: flex; flex-direction: column; gap: 8px;">
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Total Earnings</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(totalEarnings, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Total Adjustments</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(totalAdjustments, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Total Deductions</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(totalDeductions, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; padding-top: 4px;">
                    <span style="font-size: 16px; font-weight: 700; text-transform: uppercase;">Grand Total</span>
                    <span style="font-size: 16px; font-weight: 700;">{{ formatCurrency(grandTotal, 'COP', true) }}</span>
                  </div>
                </div>
                <div style="flex: 1; display: flex; flex-direction: column; gap: 8px; margin-left: 24px;">
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Morning Commission</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(morningCommission, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Evening Commission</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(eveningCommission, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Night Commission</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(nightCommission, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; padding-top: 4px;">
                    <span style="font-size: 16px; font-weight: 700; text-transform: uppercase;">Total Commission</span>
                    <span style="font-size: 16px; font-weight: 700;">{{ formatCurrency(totalCommission, 'COP', true) }}</span>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </v-card>
        </v-col>
      </v-row>

      <!-- Cards -->
      <v-row style="margin-top: 10px;">
        <!-- View Model Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'viewModel' ? 'active-card' : '']" @click="setActiveCard('viewModel')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-account-search</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">View Model</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; padding-bottom: 40px;">
              <v-select
                v-model="modelFilter"
                :items="filteredModels.map(model => `${model.fullName} (${model.stageName})`)"
                label="Select Model"
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                style="font-size: 14px;"
              ></v-select>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
              <v-btn :class="['btn-secondary', activeCard === 'viewModel' ? 'active-button' : '']" style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" @click="viewModel">View</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- USD > COP Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'usdToCop' ? 'active-card' : '']" @click="setActiveCard('usdToCop')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-currency-usd</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">USD > COP</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; padding-bottom: 40px;">
              <v-text-field v-model.number="usdXe" label="Xe Rate" type="number" outlined hide-details prepend-inner-icon="mdi-swap-horizontal" style="font-size: 14px;" :disabled="isPeriodLocked"></v-text-field>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
              <v-btn :class="['btn-secondary', activeCard === 'usdToCop' ? 'active-button' : '']" style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" @click="setUsdXe" :disabled="!usdXe || isPeriodLocked">Set</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- EUR > COP Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'eurToCop' ? 'active-card' : '']" @click="setActiveCard('eurToCop')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-currency-eur</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">EUR > COP</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; padding-bottom: 40px;">
              <v-text-field v-model.number="eurXe" label="Xe Rate" type="number" outlined hide-details prepend-inner-icon="mdi-swap-horizontal" style="font-size: 14px;" :disabled="isPeriodLocked"></v-text-field>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
              <v-btn :class="['btn-secondary', activeCard === 'eurToCop' ? 'active-button' : '']" style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" @click="setEurXe" :disabled="!eurXe || isPeriodLocked">Set</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Period Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'period' ? 'active-card' : '']" @click="setActiveCard('period')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar-multiple-check</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Period</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px;">
              <v-select 
                v-model="selectedPeriod" 
                :items="periodOptions" 
                item-title="text" 
                item-value="value" 
                outlined 
                style="font-size: 14px;" 
                @update:modelValue="value => { selectedPeriod = value; fetchModels(); }"
              ></v-select>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
              <v-btn 
                :class="['btn-secondary', activeCard === 'period' ? 'active-button' : '']" 
                style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" 
                @click="viewPeriod" 
                :disabled="!selectedPeriod"
              >
                View
              </v-btn>
              <v-btn 
                icon 
                rounded="full" 
                style="height: 36px; width: 36px; margin-left: 8px;" 
                @click="toggleLock" 
                :disabled="!selectedPeriod || !isAdmin"
              >
                <v-icon :color="isPeriodLocked ? 'black' : '#1976d2'">
                  {{ isPeriodLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Chips Below Cards -->
      <v-row style="margin-top: 16px;" v-if="selectedModels.length > 0">
        <v-col cols="12">
          <div style="display: flex; flex-wrap: wrap; gap: 8px;">
            <v-chip
              v-for="modelName in selectedModels"
              :key="modelName"
              closable
              @click:close="removeModelChip(modelName)"
              color="#1976d2"
              text-color="white"
              style="font-size: 14px;"
            >
              {{ modelName }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- Divider Bar -->
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; height: 48px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white; border-radius: 6px; margin-top: 28px;">
        <h2 style="font-size: 18px; font-weight: 600; color: white;">Model Payments</h2>
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              style="text-transform: none; font-size: 1rem; background-color: transparent; color: white;"
            >
              <span style="font-weight: 400; margin-right: 4px;">Sort by:</span>
              {{ sortOption }}
              <v-icon small style="color: white;">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              density="compact"
              v-for="option in sortOptions"
              :key="option"
              @click="sortModels(option)"
            >
              <v-list-item-title>{{ option }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Model Cards -->
      <v-card v-for="model in filteredModels" :key="model.stageName" class="elevation-2" style="padding: 8px 16px; margin-top: 20px; border-radius: 6px;">
        <v-row align="center" justify="space-between">
          <v-col cols="4">
            <h2 style="font-size: 18px; font-weight: 600; margin-bottom: 4px;">{{ model.fullName }}</h2>
            <p style="color: #666; font-size: 14px; margin-bottom: 0;">{{ model.stageName }}</p>
          </v-col>
          <v-col cols="3" style="text-align: left;">
            <p style="font-size: 14px; margin-bottom: 4px;">TRM USD: <strong>{{ usdXe !== null ? usdXe.toLocaleString('en-US') : 'N/A' }}</strong></p>
            <p style="font-size: 14px; margin-bottom: 0;">TRM EUR: <strong>{{ eurXe !== null ? eurXe.toLocaleString('en-US') : 'N/A' }}</strong></p>
          </v-col>
          <v-col cols="3" style="text-align: left;">
            <p style="font-size: 14px; font-weight: 600; margin-bottom: 4px;"><strong>{{ model.paymentDate }}</strong></p>
            <p style="font-size: 14px; margin-bottom: 0;">{{ model.period }}</p>
          </v-col>
          <v-col cols="2">
            <div style="display: flex; justify-content: left;">
              <v-img src="/logo_mgroup.png" style="max-width: 120px; object-fit: contain;"></v-img>
            </div>
          </v-col>
              
              
              </v-row>
              <v-row no-gutters style="height: 1px; margin: 0;">
                <v-col cols="12" style="padding: 0;">
                  <div style="height: 1px; background-color: #e0e0e0;"></div>
                </v-col>
              </v-row>
        
        <!-- Earnings Table -->
        <v-table density="compact" style="width: 100%; table-layout: fixed;">
          <thead>
            
            <tr>
              <th colspan="8" style="font-size: 14px; font-weight: 700; padding: 6px 8px; ">Earnings</th>
            </tr>
            <tr>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Platform</th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 25%;">Period</th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 5%;"></th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 5%;"></th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Currency</th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">Earnings</th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">Commission</th>
              <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">&nbspTotal COP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="earn in model.earnings" :key="earn.platform">
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ earn.platform }}</td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 25%;">{{ earn.period }}</td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 5%;"></td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 5%;"></td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ earn.currency }}</td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;">{{ formatCurrency(earn.earnings, earn.currency) }}</td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;">
                <span v-if="!isEditingCommissions">{{ displayCommission(earn.commission) }}</span>
                <v-text-field
                  v-else
                  v-model.number="earn.commission"
                  type="number"
                  min="0"
                  max="100"
                  dense
                  hide-details
                  style="width: 120px;"
                  @input="updateCopTotal(model, earn)"
                  :disabled="isPeriodLocked"
                ></v-text-field>
              </td>
              <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;"><span style="visibility: hidden;">-</span>$&nbsp;{{ earn.copTotal !== null ? formatCurrency(earn.copTotal, 'COP', true) : 'N/A' }}</td>
            </tr>
          </tbody>
        </v-table>

        <!-- Combined Adjustments and Deductions Table -->
<v-table
  v-if="hasAdjustments(model) || hasDeductions(model) || true"
  density="compact"
  style="width: 100%; table-layout: fixed; margin-top: 5px;"
>
  <thead v-if="hasAdjustments(model)">
    <tr>
      <th colspan="8" style="font-size: 14px; font-weight: 600; padding: 6px 8px; text-align: left;">Adjustments</th>
    </tr>
    <tr>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Type</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 25%;">Description</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Amount</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Xe</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 5%;">#</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Quotas</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">Date</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">&nbspTotal COP</th>
    </tr>
  </thead>
  <tbody v-if="hasAdjustments(model)">
    <tr v-for="adj in model.adjustments" :key="'adj-' + adj.id">
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ adj.type }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 25%;">{{ adj.description }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ formatCurrency(adj.amount, adj.currency, true) }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ adj.currency }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 5%;">{{ adj.number }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ adj.quotas }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;">{{ adj.date }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;"><span style="visibility: hidden;">-</span>$&nbsp;{{ adj.copTotal !== null ? formatCurrency(adj.copTotal * adj.sign, 'COP', true) : 'N/A' }}</td>
    </tr>
  </tbody>
  <thead v-if="hasDeductions(model)">
    <tr>
      <th colspan="8" style="font-size: 14px; font-weight: 700; text-align: left; padding: 6px 8px;">Deductions</th>
    </tr>
    <tr>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Type</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 25%;">Description</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Amount</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Xe</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 5%;">#</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 10%;">Quotas</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">Date</th>
      <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 15%;">&nbspTotal COP</th>
    </tr>
  </thead>
  <tbody v-if="hasDeductions(model)">
    <tr v-for="ded in model.deductions.filter(ded => !ded.nextPeriod)" :key="'ded-' + ded.id">
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ ded.type }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 25%;">{{ ded.description }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ formatCurrency(ded.amount, ded.currency, true) }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ ded.currency }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 5%;">{{ ded.number }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 10%;">{{ ded.quotas }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;">{{ ded.date }}</td>
      <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 15%;">{{ ded.copTotal !== null ? '- $ ' + formatCurrency(ded.copTotal, 'COP', true) : 'N/A' }}</td>
    </tr>
  </tbody>
  <tbody>
    <tr style="line-height: 0;">
    <td colspan="8" style="border-top: 1px solid #e0e0e0; padding: 0; height: 1px;"></td>
  </tr>
    <tr>
      <!-- Empty cells to match the column structure -->
      <td style="padding: 8px; width: 12%;"></td>
      <td style="padding: 8px; width: 25%;"></td>
      <td style="padding: 8px; width: 10%;"></td>
      <td style="padding: 8px; width: 10%;"></td>
      <td style="padding: 8px; width: 5%;"></td>
      <td style="padding: 8px; width: 10%;"></td>
      <td style="text-align: right; font-weight: 600; font-size: 14px; padding: 8px; width: 15%;">
        Grand Total:
      </td>
      <td style="font-weight: 700; font-size: 14px; padding: 4px 8px; text-align: left; width: 15%;">
        <span style="visibility: hidden;">-</span>$&nbsp;{{ model.totalEarnings !== null ? formatCurrency(model.totalEarnings, 'COP', true) : 'N/A' }} 
      </td>
    </tr>
  </tbody>
</v-table>
      </v-card>

      <!-- Hidden ExportPayments component -->
      <ExportPayments
        v-show="false"
        ref="exportPayments"
        :models="filteredModels"
        :usdXe="usdXe"
        :eurXe="eurXe"
        :period="selectedPeriod"
        :showCedula="true"
      />
    </template>
  <v-btn
    v-if="isEditingCommissions"
    class="btn-secondary"
    @click="toggleEditCommissions"
    style="position: fixed; bottom: 24px; right: 24px; z-index: 999; padding: 0 16px; height: 36px; background-color: #1976d2; color: white; border: none;"
  >
    <v-icon size="20" color="white">mdi-percent</v-icon>
  </v-btn>
  </div>
</template>

<script>
import secureAxios from '@/utils/secureAxios';
import ExportPayments from '@/components/ExportPayments.vue';
import { toast } from 'vue3-toastify';

export default {
  components: {
    ExportPayments
  },
  data() {
    return {
      selectedPeriod: null,
      periodOptions: [],
      activeCard: null,
      activeTab: 'studio',
      usdXe: null, // No default
      eurXe: null, // No default
      loading: true,
      modelFilter: null,
      selectedModels: [],
      downloadFormat: 'pdf',
      isPeriodLocked: false,
      isCollapsed: false,
      isEditingCommissions: false,
      lastExported: 'N/A',
      models: [],
      sortOption: 'Name A → Z',
      sortOptions: [
        'Earnings High',
        'Earnings Low',
        'Name A → Z',
        'Name Z → A',
        'Artistic A → Z',
        'Artistic Z → A'
      ],
      isAdmin: false
    };
  },
  computed: {
    filteredModels() {
      let filtered = Array.isArray(this.models) ? [...this.models] : [];

      filtered = filtered.filter(model => {
        const hasSales = model.earnings && model.earnings.length > 0;
        const hasAdjustments = model.adjustments && model.adjustments.length > 0;
        const hasSubsidy = model.subsidy && model.subsidy !== 'None';
        return hasSales || hasAdjustments || hasSubsidy;
      });

      if (this.activeTab === 'studio') {
        filtered = filtered.filter(model =>
          ['Morning', 'Evening', 'Night'].includes(model.location)
        );
      } else {
        filtered = filtered.filter(model =>
          model.location && model.location.toLowerCase() === this.activeTab.toLowerCase()
        );
      }

      if (this.selectedModels.length > 0) {
        filtered = filtered.filter(model =>
          this.selectedModels.includes(model.fullName)
        );
      }

      return this.sortModels(this.sortOption, filtered);
    },
    totalEarnings() {
      return this.filteredModels.reduce((sum, model) =>
        sum + (model.earnings.reduce((eSum, earn) => eSum + (earn.copTotal || 0), 0)), 0);
    },
    totalAdjustments() {
      return this.filteredModels.reduce((sum, model) =>
        sum + (model.adjustments.reduce((aSum, adj) => adj.nextPeriod ? aSum : aSum + ((adj.copTotal || 0) * adj.sign), 0)), 0);
    },
    totalDeductions() {
      return this.filteredModels.reduce((sum, model) =>
        sum + Math.abs(model.deductions.reduce((dSum, ded) => ded.nextPeriod ? dSum : dSum + ((ded.copTotal || 0) * ded.sign), 0)), 0);
    },
    grandTotal() {
      return this.totalEarnings + this.totalAdjustments - this.totalDeductions;
    },
    morningCommission() {
      const morningEarnings = this.filteredModels
        .filter(model => model.location === 'Morning')
        .reduce((sum, model) => 
          sum + model.earnings.reduce((eSum, earn) => 
            eSum + ((earn.copTotal || 0)), 0), 0);
      return morningEarnings * 0.02;
    },
    eveningCommission() {
      const eveningEarnings = this.filteredModels
        .filter(model => model.location === 'Evening')
        .reduce((sum, model) => 
          sum + model.earnings.reduce((eSum, earn) => 
            eSum + ((earn.copTotal || 0)), 0), 0);
      return eveningEarnings * 0.02;
    },
    nightCommission() {
      const nightEarnings = this.filteredModels
        .filter(model => model.location === 'Night')
        .reduce((sum, model) => 
          sum + model.earnings.reduce((eSum, earn) => 
            eSum + ((earn.copTotal || 0)), 0), 0);
      return nightEarnings * 0.02;
    },
    totalCommission() {
      return this.morningCommission + this.eveningCommission + this.nightCommission;
    }
  },
  watch: {
    usdXe(newVal) {
      if (newVal !== null && this.models.length > 0) {
        console.log(`usdXe changed to ${newVal}, recalculating adjustments`);
        this.recalculateAdjustments();
      }
    },
    eurXe(newVal) {
      if (newVal !== null && this.models.length > 0) {
        console.log(`eurXe changed to ${newVal}, recalculating adjustments`);
        this.recalculateAdjustments();
      }
    }
  },
  methods: {
    async fetchPeriods() {
      try {
        const response = await secureAxios.get('/api/payments/periods');
        this.periodOptions = response.data.map(period => ({
          value: period.id,
          text: period.name,
          payment_date: period.payment_date
        }));
        this.periodOptions.sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));
        const today = new Date();
        const currentPeriod = this.periodOptions.find(period => {
          const paymentDate = new Date(period.payment_date);
          return today <= paymentDate;
        }) || this.periodOptions[this.periodOptions.length - 1];
        this.selectedPeriod = currentPeriod.value;
        await this.fetchModels(); // Fetch models directly, TRM comes from here
        this.loading = false;
      } catch (error) {
        console.error('❌ Error fetching periods:', error);
        toast.error('Failed to fetch periods');
        this.loading = false;
      }
    },
    async fetchModels() {
      if (!this.selectedPeriod) return;
      try {
        const response = await secureAxios.get('/api/payments/models', {
          params: { periodId: this.selectedPeriod }
        });
        console.log('Raw backend response:', response.data);
        this.models = response.data.models.map(model => {
          const earnings = model.earnings.map(earn => {
            const trmRate = earn.currency === 'USD' ? this.usdXe : earn.currency === 'EUR' ? this.eurXe : 1;
            return {
              ...earn,
              commission: earn.commission !== null ? earn.commission : (earn.platform === 'Flirt4Free' ? 100 : 50),
              copTotal: trmRate !== null ? earn.earnings * trmRate * (earn.commission / 100) : null
            };
          });
          const adjustments = model.adjustments.map(adj => {
            const trmRate = adj.currency === 'USD' ? this.usdXe : adj.currency === 'EUR' ? this.eurXe : 1;
            const fullCopTotal = trmRate !== null ? adj.amount * trmRate : null;
            return { ...adj, fullCopTotal, copTotal: fullCopTotal !== null ? fullCopTotal / (adj.quotas || 1) : null, sign: 1 };
          });
          const deductions = model.deductions.map(ded => {
            const trmRate = ded.currency === 'USD' ? this.usdXe : ded.currency === 'EUR' ? this.eurXe : 1;
            const fullCopTotal = trmRate !== null ? ded.amount * trmRate : null;
            return { ...ded, fullCopTotal, copTotal: fullCopTotal !== null ? fullCopTotal / (ded.quotas || 1) : null, sign: -1 };
          });
          return { ...model, earnings, adjustments, deductions, totalEarnings: null };
        }) || [];
        console.log('Processed frontend models:', this.models);
        this.isPeriodLocked = response.data.isLocked || false;
        this.isAdmin = response.data.isAdmin || false;
        this.usdXe = response.data.trmUSD !== undefined ? response.data.trmUSD : this.usdXe;
        this.eurXe = response.data.trmEUR !== undefined ? response.data.trmEUR : this.eurXe;
        this.models.forEach(model => {
          this.addSubsidyAdjustment(model);
          this.recalculateTotal(model);
        });
      } catch (error) {
        console.error('❌ Error fetching models:', error);
        toast.error('Failed to fetch models');
        this.models = [];
      }
    },
    sortModels(option, modelsToSort = this.models) {
      this.sortOption = option;
      const sortedModels = [...modelsToSort];
      switch (option) {
        case 'Earnings High':
          sortedModels.sort((a, b) => (b.totalEarnings || 0) - (a.totalEarnings || 0));
          break;
        case 'Earnings Low':
          sortedModels.sort((a, b) => (a.totalEarnings || 0) - (b.totalEarnings || 0));
          break;
        case 'Name A → Z':
          sortedModels.sort((a, b) => a.fullName.localeCompare(b.fullName));
          break;
        case 'Name Z → A':
          sortedModels.sort((a, b) => b.fullName.localeCompare(a.fullName));
          break;
        case 'Artistic A → Z':
          sortedModels.sort((a, b) => a.stageName.localeCompare(b.stageName));
          break;
        case 'Artistic Z → A':
          sortedModels.sort((a, b) => b.stageName.localeCompare(a.stageName));
          break;
      }
      return sortedModels;
    },
    formatCurrency(value, currency, hideSymbol = false) {
      if (value === null || value === undefined || isNaN(value)) return 'N/A';
      if (currency !== 'COP') {
        return new Intl.NumberFormat('en-US', {
          style: hideSymbol ? 'decimal' : 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }
      const formatted = Number(value);
      const hasDecimals = formatted % 1 !== 0;
      return new Intl.NumberFormat('en-US', {
        style: hideSymbol ? 'decimal' : 'currency',
        currency: 'COP',
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0
      }).format(value);
    },
    displayCommission(commission) {
      if (commission === null || commission === undefined || isNaN(commission)) return '0%';
      return `${commission}%`;
    },
    setActiveCard(card) {
      this.activeCard = card;
    },
    viewModel() {
      if (this.modelFilter) {
        const fullName = this.modelFilter.split(' (')[0]; // Extract fullName before " ("
        const match = this.filteredModels.find(model => model.fullName === fullName);
        if (match && !this.selectedModels.includes(match.fullName)) {
          this.selectedModels.push(match.fullName);
        }
        this.modelFilter = null;
      }
    },
    removeModelChip(modelName) {
      this.selectedModels = this.selectedModels.filter(name => name !== modelName);
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    toggleDownloadFormat() {
      this.downloadFormat = this.downloadFormat === 'pdf' ? 'csv' : 'pdf';
    },
    async downloadData() {
      if (!this.$refs.exportPayments) {
        console.error('ExportPayments component not found');
        toast.error('Export component not found');
        return;
      }
      try {
        if (this.downloadFormat === 'pdf') {
          await this.$refs.exportPayments.downloadPDF();
          toast.success('PDF downloaded successfully');
        } else {
          await this.$refs.exportPayments.downloadCSV();
          toast.success('CSV downloaded successfully');
        }
        this.lastExported = new Date().toLocaleString('en-US', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('❌ Error during export:', error);
        toast.error('Failed to download data');
      }
    },
    async setUsdXe() {
      if (!this.usdXe || this.isPeriodLocked) return;
      try {
        await secureAxios.post('/api/payments/set-trm', { periodId: this.selectedPeriod, currency: 'USD', rate: this.usdXe });
        this.recalculateAdjustments();
        toast.success('USD TRM set successfully');
      } catch (error) {
        console.error('❌ Error setting USD Xe rate:', error);
        toast.error('Failed to set USD TRM');
      }
    },
    async setEurXe() {
      if (!this.eurXe || this.isPeriodLocked) return;
      try {
        await secureAxios.post('/api/payments/set-trm', { periodId: this.selectedPeriod, currency: 'EUR', rate: this.eurXe });
        this.recalculateAdjustments();
        toast.success('EUR TRM set successfully');
      } catch (error) {
        console.error('❌ Error setting EUR Xe rate:', error);
        toast.error('Failed to set EUR TRM');
      }
    },
    recalculateAdjustments() {
      this.models = this.models.map(model => {
        const earnings = model.earnings.map(earn => {
          const trmRate = earn.currency === 'USD' ? this.usdXe : earn.currency === 'EUR' ? this.eurXe : 1;
          return {
            ...earn,
            copTotal: trmRate !== null ? earn.earnings * trmRate * (earn.commission / 100) : null
          };
        });
        const adjustments = model.adjustments.map(adj => {
          const trmRate = adj.currency === 'USD' ? this.usdXe : adj.currency === 'EUR' ? this.eurXe : 1;
          const fullCopTotal = trmRate !== null ? adj.amount * trmRate : null;
          return { ...adj, fullCopTotal, copTotal: fullCopTotal !== null ? fullCopTotal / (adj.quotas || 1) : null, sign: 1 };
        });
        const deductions = model.deductions.map(ded => {
          const trmRate = ded.currency === 'USD' ? this.usdXe : ded.currency === 'EUR' ? this.eurXe : 1;
          const fullCopTotal = trmRate !== null ? ded.amount * trmRate : null;
          return { ...ded, fullCopTotal, copTotal: fullCopTotal !== null ? fullCopTotal / (ded.quotas || 1) : null, sign: -1 };
        });
        const updatedModel = { ...model, earnings, adjustments, deductions };
        this.recalculateTotal(updatedModel);
        return updatedModel;
      });
    },
    viewPeriod() {
      console.log("Viewing period:", this.selectedPeriod);
    },
    async toggleLock() {
      if (!this.isAdmin) {
        toast.error('Only admins can lock/unlock periods');
        return;
      }
      const action = this.isPeriodLocked ? 'unlock' : 'lock';
      if (confirm(`Are you sure you want to ${action} period ${this.selectedPeriod}?`)) {
        try {
          const newLockStatus = !this.isPeriodLocked;
          await secureAxios.post('/api/payments/lock-period', { 
            periodId: this.selectedPeriod, 
            lock: newLockStatus 
          });
          this.isPeriodLocked = newLockStatus;
          toast.success(`Period ${action}ed successfully`);
        } catch (error) {
          console.error('❌ Error toggling lock:', error);
          toast.error(`Failed to ${action} period`);
        }
      }
    },
    recalculateTotal(model) {
      const earningsTotal = model.earnings.reduce((sum, earn) => sum + (earn.copTotal || 0), 0);
      const adjustmentsTotal = model.adjustments.reduce((sum, adj) => adj.nextPeriod ? sum : sum + ((adj.copTotal || 0) * adj.sign), 0);
      const deductionsTotal = model.deductions.reduce((sum, ded) => ded.nextPeriod ? sum : sum + ((ded.copTotal || 0) * ded.sign), 0);
      model.totalEarnings = earningsTotal + adjustmentsTotal + deductionsTotal;
    },
    hasAdjustments(model) {
      return model.adjustments && model.adjustments.length > 0 && model.adjustments.some(adj => 
        ['Bonus', 'Adjustment', 'Subsidy'].includes(adj.type)
      );
    },
    hasDeductions(model) {
      return model.deductions && model.deductions.length > 0 && model.deductions.some(ded => 
        !ded.nextPeriod // Only include deductions not marked for next period
      );
    },
    addSubsidyAdjustment(model) {
      if (!model.subsidy || model.subsidy === 'None') return;
      const existingSubsidy = model.adjustments.find(adj => 
        adj.type === 'Subsidy' && adj.date === model.paymentDate
      );
      if (existingSubsidy) return;

      const subsidyAmount = model.subsidy === 'Half' ? 15000 : 30000;
      const paymentDate = new Date(model.paymentDate);

      const subsidyAdjustment = {
        id: `subsidy-${model.id}-${this.selectedPeriod}`,
        model_adjustment_id: model.id,
        type: 'Subsidy',
        amount: subsidyAmount,
        currency: 'COP',
        description: 'Transportation Subsidy',
        number: 1,
        quotas: 1,
        date: paymentDate.toISOString().split('T')[0],
        nextPeriod: 0,
        insufficientFunds: 0,
        observation: null,
        copTotal: subsidyAmount,
        sign: 1
      };

      model.adjustments.push(subsidyAdjustment);
    },
    toggleEditCommissions() {
      if (this.isEditingCommissions) {
        this.saveCommissions();
      }
      this.isEditingCommissions = !this.isEditingCommissions;
    },
    updateCopTotal(model, earn) {
      const trmRate = earn.currency === 'USD' ? this.usdXe : earn.currency === 'EUR' ? this.eurXe : 1;
      earn.copTotal = trmRate !== null ? earn.earnings * trmRate * (earn.commission / 100) : null;
      this.recalculateTotal(model);
    },
    async saveCommissions() {
      if (this.isPeriodLocked) {
        toast.error('Cannot edit commissions for a locked period');
        return;
      }
      const commissions = this.filteredModels.flatMap(model =>
        model.earnings.map(earn => ({
          modelId: model.id,
          platform: earn.platform,
          commission: earn.commission
        }))
      );
      try {
        const response = await secureAxios.post('/api/payments/update-commissions', {
          periodId: this.selectedPeriod,
          commissions
        });
        if (response.data.message === 'Commissions updated successfully') {
          toast.success('Commissions updated successfully');
          await this.fetchModels();
          this.sortOption = 'Name A → Z';
          this.sortModels('Name A → Z');
        }
      } catch (error) {
        console.error('❌ Error saving commissions:', error);
        toast.error('Failed to update commissions');
      }
    }
  },
  mounted() {
    this.fetchPeriods();
  }
};
</script>

<style scoped>
.active-card {
  border: 2px solid #1976d2 !important;
}
</style>